import { Collapse, Stack } from "@mui/material";
import { CheckboxInput } from "Components/Shared/Inputs/Form/CheckboxInput";
import { SocialTermsCheckboxLabel } from "Components/Social/SocialTermsConsent/SocialTermsCheckboxLabel";
import { SocialTermsConsentNote } from "Components/Social/SocialTermsConsent/SocialTermsConsentNote";
import { SocialTermsSummary } from "Components/Social/SocialTermsConsent/SocialTermsSummary";
import { SocialTermsSummaryToggle } from "Components/Social/SocialTermsConsent/SocialTermsSummaryToggle";
import { useState } from "react";
import type {
  Control,
  FieldErrors,
  FieldPath,
  FieldValues,
} from "react-hook-form";

type Props<
  TFieldValues extends FieldValues,
  TTransformedValues extends FieldValues = TFieldValues,
> = {
  control: Control<TFieldValues, unknown, TTransformedValues>;
  errors: FieldErrors<TFieldValues>;
  name: FieldPath<TFieldValues>;
};

/**
 * Consent to the chat/social terms (ZS-1749), embedded in every handle form while the persona has
 * no handle yet — setting the first handle IS the consent: checkbox naming the documents, the
 * explanatory note and the expandable contract summary with the documents to download. The host
 * form owns the schema (getHandleFormSchema with isSocialTermsConsentRequired) and disables submit
 * until ticked.
 */
export const SocialTermsConsent = <
  TFieldValues extends FieldValues,
  TTransformedValues extends FieldValues = TFieldValues,
>({
  control,
  errors,
  name,
}: Props<TFieldValues, TTransformedValues>) => {
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);

  return (
    <Stack gap={1.5}>
      <CheckboxInput
        control={control}
        errors={errors}
        name={name}
        label={<SocialTermsCheckboxLabel />}
      />
      <SocialTermsConsentNote
        onSummaryRequested={() => setIsSummaryExpanded(true)}
      />
      <SocialTermsSummaryToggle
        isExpanded={isSummaryExpanded}
        onToggle={() => setIsSummaryExpanded((isExpanded) => !isExpanded)}
      />
      <Collapse in={isSummaryExpanded} unmountOnExit>
        <SocialTermsSummary />
      </Collapse>
    </Stack>
  );
};
